/**
 * Photo-Realistic Render Engine — AI-generated renders based on estimate type
 * Adapts prompt to whatever product is being estimated (wardrobe, bed, TV unit, etc.)
 */
import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Camera, Download, Loader2, Sun, Lamp, Sparkles, Image, Eye } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '@/App';

const FINISHES = [
  'Walnut wood grain', 'Oak natural', 'Dark mahogany', 'White matte', 'Grey cement texture',
  'Teak veneer', 'Black high gloss', 'Ash wood light', 'Marble white', 'Rose gold metallic',
];
const HANDLES = ['Modern bar handles', 'Minimal recessed grip', 'Brass knob handles', 'Leather pull handles', 'No visible handles (push-open)'];

const ROOM_SCENES = [
  { id: 'room_scene', label: 'Room Scene' },
  { id: 'minimal_white', label: 'Minimal White' },
  { id: 'showroom', label: 'Showroom' },
  { id: 'lifestyle', label: 'Lifestyle' },
  { id: 'perspective', label: 'Perspective Render' },
];

const RENDER_MODES = [
  { id: 'room_scene', label: 'Room Scene', icon: Lamp },
  { id: 'product_shot', label: 'Product Shot', icon: Camera },
  { id: 'isolated', label: 'Isolated', icon: Image },
  { id: 'perspective', label: 'Perspective', icon: Eye },
];

// Detect product type from estimate name
function detectProductType(estimateName) {
  const name = (estimateName || '').toLowerCase();
  if (name.includes('bed') || name.includes('king') || name.includes('queen') || name.includes('single bed')) return 'bed';
  if (name.includes('tv') || name.includes('media') || name.includes('entertainment')) return 'tv_unit';
  if (name.includes('study') || name.includes('desk') || name.includes('bookshelf')) return 'study_desk';
  if (name.includes('side table') || name.includes('nightstand') || name.includes('bedside')) return 'side_table';
  if (name.includes('panel') || name.includes('moulding') || name.includes('mdf') || name.includes('plywood panel') || name.includes('cladding')) return 'wall_panel';
  if (name.includes('walk-in') || name.includes('closet')) return 'walk_in_closet';
  if (name.includes('loft')) return 'loft_storage';
  if (name.includes('kitchen') || name.includes('cabinet')) return 'kitchen_cabinet';
  return 'wardrobe';
}

// Get room context based on product type
function getDefaultRoom(productType) {
  const map = {
    bed: 'a luxurious master bedroom with soft ambient lighting and silk curtains',
    tv_unit: 'a modern living room with a large sofa, area rug, and natural light',
    study_desk: 'a well-lit home office with bookshelves and a comfortable chair',
    side_table: 'an elegant bedroom next to a premium bed with designer bedding',
    wall_panel: 'a sophisticated room showing the full accent wall with designer furniture',
    walk_in_closet: 'an expansive walk-in closet with island counter and chandelier',
    loft_storage: 'a bedroom showing the storage unit mounted above the doorway',
    kitchen_cabinet: 'a contemporary kitchen with marble countertops and pendant lights',
    wardrobe: 'an elegant modern bedroom with a plush bed, silk curtains, and marble flooring',
  };
  return map[productType] || map.wardrobe;
}

function getProductDescription(productType, config) {
  const w = config.width || 4, h = config.height || 8, d = config.depth || 2;
  const map = {
    bed: `a premium ${w > 5.5 ? 'king' : w > 4.5 ? 'queen' : 'single'} size bed, ${w}ft wide x ${d}ft long x ${h}ft headboard height, with ${d > 5 ? 'storage underneath' : 'solid platform base'}`,
    tv_unit: `a modern TV media console unit, ${w}ft wide x ${h}ft tall x ${d}ft deep, with shelving compartments and cable management, wall-mounted TV above`,
    study_desk: `a contemporary study desk with overhead bookshelf, ${w}ft wide x ${h}ft tall x ${d}ft deep, with integrated drawers and open shelves`,
    side_table: `a designer bedside table, ${w}ft wide x ${h}ft tall x ${d}ft deep, with drawers and open compartment`,
    wall_panel: `decorative wall panelling covering ${w}ft of wall length x ${h}ft height, with geometric moulding patterns creating elegant shadow lines`,
    walk_in_closet: `a premium walk-in closet system, ${w}ft wide x ${h}ft tall x ${d}ft deep, with hanging zones, drawers, shelves, and accessories trays`,
    loft_storage: `overhead loft storage cabinets, ${w}ft wide x ${h}ft tall x ${d}ft deep, mounted near the ceiling with lift-up doors`,
    kitchen_cabinet: `modular kitchen cabinets, ${w}ft wide x ${h}ft tall x ${d}ft deep, with soft-close drawers, handles, and upper wall units`,
    wardrobe: `a premium ${config.wardrobeType === 'sliding' ? 'sliding door' : 'hinged door'} wardrobe, ${w}ft wide x ${h}ft tall x ${d}ft deep`,
  };
  return map[productType] || map.wardrobe;
}

export default function RenderEngine({ config = {}, estimateName = '', renderedImage: renderedImageProp, onRenderUpdate, productType: productTypeProp, kitchenConfig = {} }) {
  const [generating, setGenerating] = useState(false);
  const [localRenderedImage, setLocalRenderedImage] = useState(null);
  const renderedImage = renderedImageProp !== undefined ? renderedImageProp : localRenderedImage;
  const setRenderedImage = (img) => {
    if (onRenderUpdate) onRenderUpdate(img);
    else setLocalRenderedImage(img);
  };
  const productType = productTypeProp || detectProductType(estimateName);
  const isKitchen = productType === 'kitchen';

  // Wardrobe controls
  const [finish, setFinish] = useState('Walnut wood grain');
  const [handleStyle, setHandleStyle] = useState('Modern bar handles');
  const [lighting, setLighting] = useState('warm');
  const [background, setBackground] = useState('room_scene');
  const [shutterState, setShutterState] = useState('closed');
  const [renderMode, setRenderMode] = useState(isKitchen ? 'wide_room' : 'room_scene');
  // Kitchen-specific controls
  const [kitchenLayout, setKitchenLayout] = useState(kitchenConfig.layout || 'straight');
  const [kitchenStyle, setKitchenStyle] = useState('modular');
  const [counterMaterial, setCounterMaterial] = useState(kitchenConfig.countertop?.material || 'quartz');
  const [backsplashMat, setBacksplashMat] = useState(kitchenConfig.backsplash?.material || 'tile');
  const [applianceSet, setApplianceSet] = useState('integrated');

  const token = localStorage.getItem('token');

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/render/wardrobe`, {
        width: config.width || (isKitchen ? (kitchenConfig.totalRunningFt || 10) : 4),
        height: config.height || (isKitchen ? (kitchenConfig.ceilingHeight || 10) : 8),
        depth: config.depth || 2,
        wardrobe_type: config.wardrobeType || 'hinged',
        laminate_finish: finish,
        handle_style: handleStyle,
        lighting,
        background,
        shutter_state: shutterState,
        render_mode: 'wide_room',  // always wide-room per user requirement
        product_type: productType,
        estimate_name: estimateName,
        kitchen_layout: isKitchen ? kitchenLayout : null,
        kitchen_style: isKitchen ? kitchenStyle : null,
        counter_material: isKitchen ? counterMaterial : null,
        backsplash_material: isKitchen ? backsplashMat : null,
        appliance_set: isKitchen ? applianceSet : null,
      }, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 120000,
      });
      setRenderedImage(res.data.image_base64);
      toast.success('Render generated successfully');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Render failed. Try again.');
    } finally {
      setGenerating(false);
    }
  };

  const downloadImage = () => {
    if (!renderedImage) return;
    const link = document.createElement('a');
    link.href = `data:image/png;base64,${renderedImage}`;
    link.download = `${(estimateName || 'Render').replace(/\s+/g, '_')}.png`;
    link.click();
  };

  const Sel = ({ label, value, onChange, options }) => (
    <div>
      <Label>{label}</Label>
      <select className="h-9 w-full rounded-md border border-stone-300 px-2 text-sm" value={value} onChange={e => onChange(e.target.value)}>
        {options.map(o => typeof o === 'string' ? <option key={o} value={o}>{o}</option> : <option key={o.id || o.value} value={o.id || o.value}>{o.label}</option>)}
      </select>
    </div>
  );

  return (
    <div className="space-y-5" data-testid="render-engine">
      <div className="flex items-center gap-2"><Camera size={18} className="text-violet-600" /><h3 className="font-bold text-stone-800">AI Render — {estimateName || 'Product'}</h3>
        <span className="text-xs bg-violet-50 text-violet-600 px-2 py-0.5 rounded-full capitalize">{productType.replace('_', ' ')}</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Controls */}
        <Card className="lg:col-span-1">
          <CardContent className="pt-5 pb-4 space-y-3">
            <h4 className="font-semibold text-sm text-stone-700 mb-2">{isKitchen ? 'Kitchen Render Controls' : 'Render Controls'}</h4>

            {isKitchen ? (<>
              <Sel label="Kitchen Layout" value={kitchenLayout} onChange={setKitchenLayout} options={[
                { id: 'straight', label: 'Straight' },
                { id: 'l_shape', label: 'L-Shape' },
                { id: 'u_shape', label: 'U-Shape' },
                { id: 'parallel', label: 'Parallel (Galley)' },
                { id: 'island', label: 'Island' },
                { id: 'peninsula', label: 'Peninsula' },
              ]} />
              <Sel label="Design Style" value={kitchenStyle} onChange={setKitchenStyle} options={[
                { id: 'modular', label: 'Modern Modular' },
                { id: 'modern', label: 'Handleless Modern (Gola)' },
                { id: 'traditional', label: 'Traditional Shaker' },
                { id: 'minimal', label: 'Minimal Matte' },
                { id: 'luxury', label: 'Luxury High-Gloss' },
              ]} />
              <Sel label="Countertop" value={counterMaterial} onChange={setCounterMaterial} options={[
                { id: 'granite', label: 'Granite' },
                { id: 'quartz', label: 'Quartz' },
                { id: 'marble', label: 'Marble' },
                { id: 'solid surface', label: 'Solid Surface' },
                { id: 'sintered stone', label: 'Sintered Stone' },
              ]} />
              <Sel label="Backsplash" value={backsplashMat} onChange={setBacksplashMat} options={[
                { id: 'tile', label: 'Ceramic Tile' },
                { id: 'quartz', label: 'Quartz Slab' },
                { id: 'glass', label: 'Back-Painted Glass' },
                { id: 'stone', label: 'Stone Cladding' },
              ]} />
              <Sel label="Appliances" value={applianceSet} onChange={setApplianceSet} options={[
                { id: 'integrated', label: 'Integrated Built-in' },
                { id: 'freestanding', label: 'Freestanding' },
                { id: 'none', label: 'No Appliances Shown' },
              ]} />
            </>) : (<>
              <Sel label="Laminate Finish" value={finish} onChange={setFinish} options={FINISHES} />
              <Sel label="Handle Style" value={handleStyle} onChange={setHandleStyle} options={HANDLES} />
              <Sel label="Room Scene" value={background} onChange={setBackground} options={ROOM_SCENES} />
            </>)}

            {/* Lighting (common to both) */}
            <div>
              <Label>Lighting</Label>
              <div className="flex gap-1.5 mt-1">
                {[{ id: 'day', icon: Sun, label: 'Day' }, { id: 'warm', icon: Lamp, label: 'Warm' }, { id: 'luxury', icon: Sparkles, label: 'Luxury' }].map(l => (
                  <button key={l.id} onClick={() => setLighting(l.id)}
                    className={`flex-1 flex items-center justify-center gap-1 py-2 rounded-lg text-xs font-medium transition-colors ${lighting === l.id ? 'bg-amber-100 text-amber-800 border border-amber-300' : 'bg-stone-50 text-stone-600 border border-stone-200'}`}>
                    <l.icon size={14} />{l.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-violet-50 border border-violet-200 rounded-lg p-2 text-xs text-violet-700">
              <Sparkles size={12} className="inline mr-1" />Wide-angle full-room view — showing the {isKitchen ? 'kitchen with dining/living context' : 'product in its complete room setting'}.
            </div>

            <Button className="w-full mt-3" onClick={handleGenerate} disabled={generating} data-testid="generate-render-btn">
              {generating ? <><Loader2 size={15} className="mr-2 animate-spin" />Generating (30-60s)...</> : <><Camera size={15} className="mr-2" />Generate {isKitchen ? 'Kitchen' : productType.replace('_', ' ')} Render</>}
            </Button>
          </CardContent>
        </Card>

        {/* Preview */}
        <Card className="lg:col-span-2">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-semibold text-sm text-stone-700">Preview — {estimateName || 'Product Render'}</h4>
              {renderedImage && (
                <Button size="sm" variant="outline" onClick={downloadImage} data-testid="download-render-btn"><Download size={14} className="mr-1" />Download HD</Button>
              )}
            </div>
            <div className="bg-stone-100 rounded-xl overflow-hidden flex items-center justify-center" style={{ minHeight: 400 }} data-testid="render-preview">
              {generating ? (
                <div className="flex flex-col items-center gap-3 text-stone-500">
                  <Loader2 size={40} className="animate-spin" />
                  <p className="text-sm font-medium">Creating your {productType.replace('_', ' ')} render...</p>
                  <p className="text-xs text-stone-400">This takes 30-60 seconds</p>
                </div>
              ) : renderedImage ? (
                <img src={`data:image/png;base64,${renderedImage}`} alt="Render" className="max-w-full max-h-[600px] object-contain" data-testid="rendered-image" />
              ) : (
                <div className="flex flex-col items-center gap-3 text-stone-400 py-16">
                  <Camera size={48} className="opacity-30" />
                  <p className="text-sm">Configure settings and click Generate</p>
                  <p className="text-xs">AI will render: <strong className="capitalize">{productType.replace('_', ' ')}</strong></p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
